import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Brain, ArrowLeft, ChevronDown, ChevronUp, Loader2, Globe, Building2,
  RotateCcw, History, Clock,
} from 'lucide-react';
import toast from '../utils/toast';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { InfoBadge } from '../components/UI/InfoBadge';
import { talentAssessmentsAPI } from '../services/api';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import {
  TRAIT_ORDER,
  TRAIT_META,
  ASPECT_META,
  qualitativeBucket,
} from '../config/bigFiveMeta';

/**
 * Big Five Profile — employee result page.
 *
 * Layout:
 *   - 5 trait cards in canonical order (O, C, E, A, N) with a qualitative
 *     bucket label (Low / Below average / Average / Above average / High)
 *     and a percentile bar against the chosen reference group.
 *   - Toggle: Public benchmark vs Company benchmark (only shown if enough
 *     colleagues have completed the assessment).
 *   - Click a trait to expand and see its 6 underlying aspects, each with
 *     a one-line behavioural description.
 *
 * Psychometric jargon (raw score, T-score, "facet") is intentionally not
 * surfaced to the employee — it adds noise without adding insight for a
 * non-specialist reader. The HR Admin individual-result modal shows the
 * same employee-friendly view PLUS a quiet "T-score / Raw" secondary
 * line for HR who need precise numbers for documentation/calibration.
 *
 * Quality flags are NEVER shown to the employee — they are HR-only and
 * the API already strips them from this endpoint's payload.
 *
 * The TRAIT_META, ASPECT_META, and qualitativeBucket helpers are
 * imported from `config/bigFiveMeta` so the same vocabulary, blurbs,
 * and bucket boundaries appear on the HR Admin's individual-result
 * modal too — the employee and the HR person looking at them see the
 * same words for the same scores.
 */


const SelfServiceBigFiveResult = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [normSource, setNormSource] = useState('public'); // 'public' | 'company'
  const [expandedTrait, setExpandedTrait] = useState(null);

  // Result + status fetched in parallel — status drives the retake CTA so
  // the "Take again" button respects the same cadence rule the intro page
  // does. Without this the button would always render and silently bounce
  // the user back to a "Re-take available in N days" wall.
  const load = useCallback(async () => {
    try {
      const [resultRes, statusRes] = await Promise.all([
        talentAssessmentsAPI.getMyBigFiveResult(),
        talentAssessmentsAPI.getBigFiveStatus().catch(() => ({ data: null })),
      ]);
      setData(resultRes.data);
      setStatus(statusRes.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('No Big Five Profile on file yet — complete the assessment first.');
      } else {
        toast.error('Could not load your Big Five Profile.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-blue-800 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto p-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <Brain className="w-10 h-10 text-blue-800 mx-auto" />
            <h2 className="mt-3 text-section-header">No Big Five Profile yet</h2>
            <p className="mt-2 text-body">Complete the assessment to see your result.</p>
            <Link
              to="/self-service/big-five"
              className="btn btn-primary mt-4"
            >
              Go back
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const traitScores = data.result?.trait_scores || {};
  const aspectScores = data.result?.facet_scores || {};
  const companyAvailable = !!data.company_norms_available;
  const submittedAt = data.session?.submitted_at
    ? formatDateTime(data.session.submitted_at)
    : null;
  const usedNormKey = normSource === 'company' && companyAvailable ? 'percentile_company' : 'percentile_public';

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen pb-24">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-4">
            <Link
              to="/self-service/big-five"
              aria-label="Back to Big Five Profile"
              title="Back to Big Five Profile"
              className="icon-button flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="text-subsection-header truncate">Your Big Five Profile</h1>
              {submittedAt && (
                <p className="text-body mt-1">Completed {submittedAt}</p>
              )}
            </div>
          </div>
        </div>

        {/* Reference group toggle */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Compare me with</div>
              <div className="text-xs text-gray-600 mt-0.5">
                Switch the group your scores are measured against.
              </div>
            </div>
            {/* Reference-group toggle — DS canonical pill tabs (employee
                theme), -sm variant. The "Our colleagues" tab is disabled
                until enough colleagues complete the assessment. */}
            <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
              <button
                type="button"
                onClick={() => setNormSource('public')}
                className={`tab-btn tab-btn-sm ${normSource === 'public' ? 'active' : ''}`}
                aria-pressed={normSource === 'public'}
              >
                <Globe className="w-4 h-4" />
                People worldwide
              </button>
              <button
                type="button"
                onClick={() => companyAvailable && setNormSource('company')}
                disabled={!companyAvailable}
                className={`tab-btn tab-btn-sm ${normSource === 'company' ? 'active' : ''} ${!companyAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
                aria-pressed={normSource === 'company'}
                title={companyAvailable ? '' : 'Available once enough colleagues complete the assessment.'}
              >
                <Building2 className="w-4 h-4" />
                Our colleagues
              </button>
            </div>
          </div>
          {!companyAvailable && (
            <div className="mt-3 text-xs text-gray-500">
              The "Our colleagues" view becomes available once at least 5 colleagues complete the Big Five Profile, so no one can be identified from a chart.
            </div>
          )}
        </div>

        {/* Trait cards */}
        <div className="grid grid-cols-1 gap-4">
          {TRAIT_ORDER.map((traitCode) => {
            const trait = traitScores[traitCode];
            if (!trait) return null;
            const meta = TRAIT_META[traitCode];
            const pct = trait[usedNormKey];
            const expanded = expandedTrait === traitCode;
            return (
              <div key={traitCode} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedTrait(expanded ? null : traitCode)}
                  className={`w-full text-left bg-gradient-to-r ${meta.gradient} p-5 sm:p-6 hover:opacity-95 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
                  aria-expanded={expanded}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-7 rounded-sm ${meta.bar}`} />
                        <h2 className="text-section-header">{meta.name}</h2>
                      </div>
                      <p className="text-body mt-1">{meta.caption}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <PercentilePill value={pct} barClass={meta.bar} />
                    </div>
                  </div>
                  {/* Percentile bar */}
                  <div className="mt-5">
                    <div className="relative h-3 bg-white rounded-full overflow-hidden border border-gray-200">
                      {/* Mid mark */}
                      <div className="absolute inset-y-0 left-1/2 w-px bg-gray-300 z-10" />
                      <div
                        className={`h-full ${meta.bar} transition-all duration-500`}
                        style={{ width: `${Math.max(0, Math.min(100, pct ?? 0))}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
                      <span>{meta.low}</span>
                      <span>{meta.high}</span>
                    </div>
                  </div>
                  {/* Qualitative summary — a single plain-English label
                      replaces the previous trio of percentile / T-score /
                      raw. The big percentile pill at the top right of
                      this card already shows the precise number.
                      Rendered via the DS `<InfoBadge>` so the chip uses
                      the canonical 50/700/600-20 palette + sizing. */}
                  {(() => {
                    const bucket = qualitativeBucket(pct);
                    return bucket ? (
                      <div className="mt-4 flex items-center gap-2 text-sm text-gray-700">
                        <InfoBadge color={bucket.color} label={bucket.label} />
                        <span className="text-xs text-gray-500">
                          compared to {normSource === 'company' && companyAvailable ? 'our colleagues' : 'people worldwide'}
                        </span>
                      </div>
                    ) : null;
                  })()}

                  <div className="mt-4 inline-flex items-center text-xs font-semibold text-gray-700">
                    {expanded ? (
                      <>Hide aspects <ChevronUp className="w-4 h-4 ml-1" /></>
                    ) : (
                      <>Show 6 aspects <ChevronDown className="w-4 h-4 ml-1" /></>
                    )}
                  </div>
                </button>

                {expanded && (
                  <div className="p-5 sm:p-6 border-t border-gray-100 bg-white">
                    {/* One-line preamble so the drill-down feels guided
                        rather than just numbers under more numbers. */}
                    <div className="mb-4 text-xs text-gray-600">
                      The six aspects below break <strong>{meta.name}</strong> into the more specific tendencies that make it up.
                    </div>

                    <ul className="space-y-3">
                      {[1, 2, 3, 4, 5, 6].map((idx) => {
                        const aspectCode = `${traitCode}${idx}`;
                        const aspect = aspectScores[aspectCode];
                        if (!aspect) return null;
                        const aspectPct = aspect[usedNormKey];
                        const aspectMeta = ASPECT_META[aspectCode] || { name: aspectCode, blurb: '' };
                        return (
                          <li key={aspectCode} className="p-3 border border-gray-200 rounded-xl">
                            {/* Top row: name + score */}
                            <div className="flex items-baseline justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-900">
                                  {aspectMeta.name}
                                </div>
                              </div>
                              <span className={`text-base font-bold ${meta.bar.replace('bg-', 'text-')} tabular-nums flex-shrink-0`}>
                                {aspectPct != null ? `${aspectPct.toFixed(0)}%` : '—'}
                              </span>
                            </div>

                            {/* Blurb — short, behavioural, glanceable */}
                            {aspectMeta.blurb && (
                              <p className="mt-1 text-xs text-gray-600 leading-relaxed">
                                {aspectMeta.blurb}
                              </p>
                            )}

                            {/* Percentile bar */}
                            <div className="mt-2 relative h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="absolute inset-y-0 left-1/2 w-px bg-gray-300 z-10" />
                              <div
                                className={`h-full ${meta.bar} transition-all duration-500`}
                                style={{ width: `${Math.max(0, Math.min(100, aspectPct ?? 0))}%` }}
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* What this means + actions */}
        <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-6">
          <h3 className="text-subsection-header">How to read this</h3>
          <ul className="mt-3 space-y-2 text-sm text-gray-700">
            <li className="flex gap-2"><span className="text-blue-800">•</span><span>The bar shows where your score sits compared with the group you've chosen — the line down the middle marks the typical score.</span></li>
            <li className="flex gap-2"><span className="text-blue-800">•</span><span>Big Five scores are descriptive, not prescriptive — there are no "good" or "bad" results.</span></li>
            <li className="flex gap-2"><span className="text-blue-800">•</span><span>Personality is fairly stable, so we recommend re-taking once a year.</span></li>
          </ul>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link
              to="/self-service/big-five"
              className="btn btn-sm btn-outline"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
            <Link
              to="/self-service/big-five/access-log"
              className="btn btn-sm btn-outline"
            >
              <History className="w-4 h-4" />Who has viewed me
            </Link>
            {/* Re-take CTA respects the same 12-month cadence enforced
                server-side and on the intro page. We show the action only
                when the user is actually eligible — otherwise we surface
                the next-eligible date inline so the user isn't sent
                bouncing between screens. */}
            {status?.retake_available ? (
              <Link
                to="/self-service/big-five"
                className="btn btn-sm btn-primary"
              >
                <RotateCcw className="w-4 h-4" />Take again
              </Link>
            ) : status ? (
              // DS-canonical chip pattern: 50 bg / 700 text / 600/20 ring,
              // sized to match `<InfoBadge>` (px-2.5 py-0.5 text-xs
              // font-medium). The Clock icon retains the temporal
              // "waiting" semantic from the warning tier in
              // `StatusBadge`'s tier-icon table.
              <span
                className="inline-flex items-center gap-1 rounded-full ring-1 ring-inset ring-amber-600/20 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700"
                title={
                  status.retake_available_on
                    ? `Re-take unlocks on ${formatDate(status.retake_available_on)}.`
                    : ''
                }
              >
                <Clock className="w-3 h-3" />
                {status.retake_days_remaining != null && status.retake_days_remaining > 0
                  ? <>Re-take in {status.retake_days_remaining} day{status.retake_days_remaining === 1 ? '' : 's'}</>
                  : status.retake_available_on
                    ? <>Re-take from {formatDate(status.retake_available_on)}</>
                    : <>Re-take not yet available</>}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const PercentilePill = ({ value, barClass }) => {
  if (value == null) {
    return <span className="text-xs text-gray-500">—</span>;
  }
  return (
    <div
      className="inline-flex flex-col items-end"
      title={`Your score is higher than ${value.toFixed(0)}% of people in the reference group.`}
    >
      <span className={`text-3xl font-bold ${barClass.replace('bg-', 'text-')} leading-none tabular-nums`}>
        {value.toFixed(0)}<span className="text-lg">%</span>
      </span>
      <span className="text-[10px] text-gray-500 mt-1">
        score lower than you
      </span>
    </div>
  );
};

export default SelfServiceBigFiveResult;
